import config from 'constants/config'
import { mapObject } from 'lib/object/objectUtils'
import { sanitizeErrorMessage, sanitizeForLogging } from './redactPii'

export function getSentryServerSideReleaseName(defaultName: string) {
  // REACT_APP_COMMIT_SHA is from environment variable: i.e. 1234567890abcdef
  const commitSha = process.env.REACT_APP_COMMIT_SHA || undefined
  return commitSha ? `${process.env.REACT_APP_SENTRY_ENV}-${commitSha?.substring(0, 7)}` : defaultName
}

export function getSentryClientSideReleaseName(defaultName: string) {
  // config should have COMMIT_SHA value from REACT_APP_COMMIT_SHA
  return config.COMMIT_SHA ? `${config.SENTRY_ENV}-${config.COMMIT_SHA?.substring(0, 7)}` : defaultName
}

/**
 * Sentry beforeSend hook: runs for every event (explicit reports + unhandled exceptions + captureMessage).
 * Redacts emails from exception messages and event.message so PII never leaves the client/server.
 */
export function beforeSend(event: any) {
  if (event?.exception?.values) {
    for (const exc of event.exception.values) {
      if (exc?.value && typeof exc.value === 'string') {
        exc.value = sanitizeErrorMessage(exc.value)
      }
    }
  }
  if (event?.message != null) {
    if (typeof event.message === 'string') {
      event.message = sanitizeErrorMessage(event.message)
    } else if (typeof event.message === 'object' && typeof event.message.formatted === 'string') {
      event.message.formatted = sanitizeErrorMessage(event.message.formatted)
    }
  }
  if (event?.logentry) {
    const le = event.logentry
    if (typeof le.message === 'string') le.message = sanitizeErrorMessage(le.message)
    if (typeof le.formatted === 'string') le.formatted = sanitizeErrorMessage(le.formatted)
  }
  return event
}

let sentry: any
export function initSentryServerSide(Sentry: any) {
  sentry = Sentry
  sentry.init({
    dsn: process.env.REACT_APP_SENTRY_ID,
    environment: process.env.REACT_APP_SENTRY_ENV,
    release: getSentryServerSideReleaseName('unknown'),
    beforeSend,
  })

  sentry.setTag('brand', `${config.BRAND}`)
}

export function initSentryClientSide(Sentry: any) {
  sentry = Sentry
  sentry.init({
    dsn: config.SENTRY_ID,
    environment: config.SENTRY_ENV,
    release: getSentryClientSideReleaseName('unknown'),
    beforeSend,
    integrations: [
      /**
       * We must use `drop-error-if-exclusively-contains-third-party-frames` to avoid sending errors from third party libraries.
       * If use `drop-error-if-contains-third-party-frames`, it will drop all errors that contain at least one third-party stack frame.
       *
       * "drop-error-if-contains-third-party-frames": Drop error events that contain at least one third-party stack frame.
       * "drop-error-if-exclusively-contains-third-party-frames": Drop error events that exclusively contain third-party stack frames.
       * "apply-tag-if-contains-third-party-frames": Keep all error events, but apply a third_party_code: true tag in case the error contains at least one third-party stack frame.
       * "apply-tag-if-exclusively-contains-third-party-frames": Keep all error events, but apply a third_party_code: true tag in case the error contains exclusively third-party stack frames.
       */
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ['luxgroup-customer-portal'],
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
      }),
    ],
  })
  sentry.setTag('brand', `${config.BRAND}`)
}

export async function sendErrorToSentry(
  error: Error | any,
  user?: App.AuthAccount,
  additional: Record<string, any> = {},
) {
  try {
    // Remove PII from extras (cart, meta, caller context) before sending to Sentry
    const obfuscatedExtras = sanitizeForLogging(additional)
    const stringifiedObfuscatedExtras = mapObject(obfuscatedExtras, (extra) => {
      if (typeof extra !== 'object' && !Array.isArray(extra)) return extra
      return JSON.stringify(extra, null, 2)
    })

    // Set user context with ID only—never email or other PII
    if (user?.memberId) {
      sentry.setUser({ id: user.memberId })
    }

    sentry.withScope((scope: any) => {
      // Sanitize error message to redact emails and other PII in free text
      const rawMessage = error instanceof Error ? error.message : JSON.stringify(error)
      const sanitizedMessage = sanitizeErrorMessage(rawMessage)
      const exception = new Error(sanitizedMessage)

      // Preserve stack trace; sanitize first line since it repeats the message and may contain PII
      if (error instanceof Error && error.stack) {
        const firstNewline = error.stack.indexOf('\n')
        const firstLine = firstNewline >= 0 ? error.stack.slice(0, firstNewline) : error.stack
        const rest = firstNewline >= 0 ? error.stack.slice(firstNewline) : ''
        exception.stack = sanitizeErrorMessage(firstLine) + rest
      }

      // Omit custom fingerprint so Sentry groups by stack trace (avoids over-grouping generic messages like "Unknown error")
      scope.setExtras(stringifiedObfuscatedExtras)
      sentry.captureException(exception)
    })
  } catch {}
}
